// eslint-disable-next-line import/no-unresolved
import { Box, RuleConfirmation, Spinner } from "@ui-kit/components/index.es";
import React, { useEffect } from "react";

import { connect } from "react-redux";
import { compose } from "redux";
import { goBack, push } from "react-router-redux";

import moment from "moment-timezone";
import Head from "pages/_components/Head";
import Notification from "pages/_components/Notification";
import { arrayOf, bool, func, shape, string } from "prop-types";
import { actions as echeqRuleActions, selectors as echeqRuleSelectors } from "reducers/echeqRule";
import { selectors } from "reducers/echeqRules";
import { actions as tokenActions, selectors as tokenSelectors } from "reducers/token";
import { getAccountDescriptionLimited } from "util/accounts";
import { getAccountNumber, getCurrencyIso, getProductType } from "util/cbuCmf";
import * as i18n from "util/i18n";
import isTokenActive from "util/token";
import Yup from "yup";
import { resizableRoute } from "pages/_components/Resizable";
import { applyEmbeddedV3ShellLayoutState, clearEmbeddedV3ShellLayoutState } from "util/v3ShellLayoutSync";

const FORM_ID = "echeqs.rule.list";

function EcheqRuleSummary(props) {
    const {
        dispatch,
        isDesktop,
        isFetching,
        tokenNumber,
        summarySelectedRule: { selectedRule, action },
        fetchingToken,
        conditions,
        subConditionsAmount,
        subConditionsIssuer,
        subConditionsReceived,
        embeddedInV3Shell,
    } = props;

    useEffect(() => {
        if (!isDesktop && isTokenActive()) {
            dispatch(tokenActions.softTokenGenerationRequest());
        }
        return () => {
            dispatch(echeqRuleActions.echeqRuleCleanData());
        };
    }, []);

    useEffect(() => {
        if (!embeddedInV3Shell || !selectedRule?.idRule) {
            return undefined;
        }
        const headerTitle = i18n.get(`echeqs.rule.actionConfirmation.${action}.title`, null, {
            RULE_NAME: selectedRule.ruleName,
        });
        applyEmbeddedV3ShellLayoutState(dispatch, {
            embeddedInV3Shell,
            isDesktop,
            scopes: ["echeqsRule"],
            headerTitle,
            goBackPath: "/v2/echeqRules",
        });
        return () => {
            clearEmbeddedV3ShellLayoutState(dispatch);
        };
    }, [dispatch, embeddedInV3Shell, isDesktop, action, selectedRule?.idRule, selectedRule?.ruleName]);

    const handleBack = () => {
        if (embeddedInV3Shell) {
            dispatch(push("/v2/echeqRules"));
        } else {
            dispatch(goBack());
        }
    };

    const otpSchema = Yup.object({
        otp:
            !isDesktop && isTokenActive() && tokenNumber
                ? Yup.string().nullable()
                : Yup.string().required(i18n.get("debin.programmed.confirmation.otp.required")),
    });

    const renderMapedConditions = (ruleConditions) =>
        (ruleConditions || []).map((item) => ({
            type: item.condition,
            operator: item.subCondition,
            value: item.value,
        }));

    const getToken = async () => {
        if (!isDesktop && isTokenActive() && tokenNumber) {
            return { token: tokenNumber };
        }
        return { token: null };
    };

    const confirmationProps = {
        loading: isFetching,
        confirmationText: i18n.get("form.credential.hint"),
        getToken,
        validationConfirm: otpSchema,
        handleConfirm: ({ otp }) => {
            const finalOtp = !isDesktop && isTokenActive() && tokenNumber ? tokenNumber : otp;
            dispatch(echeqRuleActions.setGoBackEcheqsRule(true));
            dispatch(
                echeqRuleActions.echeqRuleSend({
                    numberRule: selectedRule.idRule,
                    _otp: finalOtp,
                    actionType: action,
                }),
            );
        },
        handleCatchError: () => {},
    };

    const getAccountFromCBU = (cbu) => {
        const number = getAccountNumber(cbu);
        const productType = getProductType(cbu);
        const currency = getCurrencyIso(cbu);
        return getAccountDescriptionLimited({ number, productType, currency });
    };

    const getHeader = () => {
        if (embeddedInV3Shell && !isDesktop) {
            return null;
        }
        if (isDesktop) {
            return (
                <>
                    <Head onBack={handleBack} />
                    <Head
                        titleText={i18n.get(`echeqs.rule.actionConfirmation.${action}.title`, null, {
                            RULE_NAME: selectedRule.ruleName,
                        })}
                        headerClassName="mt-3 mb-4"
                        titleClassName="no-wrap"
                        {...(action !== "delete" && { messageTooltip: "echeqs.rule.actionConfirmation.tooltip" })}
                        alwaysShowMessage
                    />
                </>
            );
        }
        return (
            <Head
                titleText={i18n.get(`echeqs.rule.actionConfirmation.${action}.title`, null, {
                    RULE_NAME: selectedRule.ruleName,
                })}
                titleClassName={!isDesktop ? "header-mobile-title-background-blue" : ""}
                headerClassName={!isDesktop ? "blue-main-header-mobile" : ""}
                centerContentClassName="mx-5"
                onBack={handleBack}
                {...(action !== "delete" && { messageTooltip: "echeqs.rule.actionConfirmation.tooltip" })}
            />
        );
    };

    if (!selectedRule?.idRule) {
        dispatch(push("/v2/echeqRules"));
        return null;
    }
    return (
        <>
            {!embeddedInV3Shell && (
                <Notification scopeToShow="echeqsRule" notificationClassname={!isDesktop ? "z-index-9" : ""} />
            )}
            <div className="mx-3">
                <div className="admin-detail-head">{getHeader()}</div>
                {isFetching || fetchingToken ? (
                    <Box flex justify="center" align="center" column py={72}>
                        <Spinner size="large" />
                    </Box>
                ) : (
                    <RuleConfirmation
                        headText={i18n.get("echeqs.rule.actionConfirmation.subtitle")}
                        rule={{
                            number: selectedRule.idRule,
                            name: selectedRule.ruleName,
                            action: selectedRule.actionType,
                            ...(selectedRule?.cbu && { account: getAccountFromCBU(selectedRule.cbu) }),
                            conditions: renderMapedConditions(selectedRule.conditions),
                            creationDate: selectedRule.creationDate
                                ? moment(selectedRule.creationDate).tz("America/Argentina/Buenos_Aires")
                                : null,
                            updatedDate: selectedRule.updateDate
                                ? moment(selectedRule.updateDate).tz("America/Argentina/Buenos_Aires")
                                : null,
                            currency: selectedRule?.currency,
                            beneficiaryAddress: selectedRule?.address,
                            beneficiary: selectedRule?.beneficiary,
                            reference: selectedRule?.reference,
                            referenceValue: selectedRule?.referenceValue,
                        }}
                        getCardLabel={(key) => i18n.get(`${FORM_ID}.card.${key}.label`)}
                        conditionsOptions={conditions}
                        operatorIssuerOptions={subConditionsIssuer}
                        operatorAmountOptions={subConditionsAmount}
                        operatorReceivedOptions={subConditionsReceived}
                        confirmationProps={confirmationProps}
                        drawerConditionsText={(ruleName) =>
                            i18n.get(`${FORM_ID}.card.context.conditions.description`, "", { RULE_NAME: ruleName })
                        }
                    />
                )}
            </div>
        </>
    );
}

EcheqRuleSummary.propTypes = {
    dispatch: func.isRequired,
    isDesktop: bool,
    summarySelectedRule: shape({}),
    isFetching: bool,
    tokenNumber: string,
    fetchingToken: bool.isRequired,
    conditions: arrayOf(shape({})),
    subConditionsAmount: arrayOf(shape({})),
    subConditionsIssuer: arrayOf(shape({})),
    subConditionsReceived: arrayOf(shape({})),
    embeddedInV3Shell: bool,
};

EcheqRuleSummary.defaultProps = {
    isDesktop: true,
    isFetching: false,
    tokenNumber: null,
    summarySelectedRule: {
        pageNumber: null,
        totalPages: null,
    },
    conditions: [],
    subConditionsAmount: [],
    subConditionsIssuer: [],
    subConditionsReceived: [],
    embeddedInV3Shell: false,
};

const mapStateToProps = (state) => ({
    isFetching: echeqRuleSelectors.getFetching(state),
    summarySelectedRule: selectors.getSummarySelectedRule(state),
    tokenNumber: tokenSelectors.getTokenNumber(state),
    fetchingToken: tokenSelectors.getFetchingToken(state),
    conditions: selectors.getConditions(state),
    subConditionsAmount: selectors.getSubConditionsAmount(state),
    subConditionsIssuer: selectors.getSubConditionsIssuer(state),
    subConditionsReceived: selectors.getSubConditionsReceived(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(EcheqRuleSummary);
